import React, { useState, useEffect } from 'react';
import { apiService } from '../../services/api';

interface JoinWorkspaceProps {
  token: string;
  isLoggedIn: boolean;
  onLoginRequired: () => void;
  onSuccess: (workspace: any) => void;
}

const JoinWorkspace: React.FC<JoinWorkspaceProps> = ({ token, isLoggedIn, onLoginRequired, onSuccess }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [invitation, setInvitation] = useState<any>(null);
  const [hasExistingAccount, setHasExistingAccount] = useState(false);
  
  // Registration form
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    validateInvitation();
  }, [token]);

  const validateInvitation = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await apiService.validateInvitation(token);
      if (response.success) {
        setInvitation(response.invitation);
        setHasExistingAccount(response.hasExistingAccount);
      }
    } catch (err: any) {
      setError(err.response?.data?.error || 'Invalid or expired invitation link');
    } finally {
      setLoading(false);
    }
  };

  const handleAcceptAsExistingUser = async () => {
    if (!isLoggedIn) {
      onLoginRequired();
      return;
    }

    try {
      setSubmitting(true);
      setError(null);
      const response = await apiService.acceptInvitation(token);
      if (response.success) {
        onSuccess(response.workspace);
      }
    } catch (err: any) {
      const errorMsg = err.response?.data?.error || 'Failed to accept invitation';
      
      // If logged in with wrong email, provide helpful guidance
      if (errorMsg.includes('different email')) {
        setError(`${errorMsg}. Please log out and log in with the account for ${invitation?.email}, or ask the workspace owner to send a new invitation to your current email.`);
      } else {
        setError(errorMsg);
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleRegisterAndJoin = async () => {
    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    if (password.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }

    try {
      setSubmitting(true);
      setError(null);
      const response = await apiService.registerViaInvitation(token, {
        firstName,
        lastName,
        password
      });

      if (response.success) {
        // Store the token
        localStorage.setItem('token', response.token);
        localStorage.setItem('user', JSON.stringify(response.user));
        onSuccess(response.workspace);
      }
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to create account');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: '#f9fafb'
      }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: '2rem', marginBottom: '16px' }}>⏳</div>
          <p style={{ color: '#6b7280' }}>Validating invitation...</p>
        </div>
      </div>
    );
  }

  if (error && !invitation) {
    return (
      <div style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: '#f9fafb'
      }}>
        <div style={{
          backgroundColor: 'white',
          padding: '40px',
          borderRadius: '16px',
          textAlign: 'center',
          maxWidth: '400px',
          boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
        }}>
          <div style={{ fontSize: '3rem', marginBottom: '16px' }}>❌</div>
          <h2 style={{ fontSize: '1.5rem', fontWeight: 700, marginBottom: '8px' }}>Invalid Invitation</h2>
          <p style={{ color: '#6b7280', marginBottom: '24px' }}>{error}</p>
          <button
            onClick={() => window.location.href = '/'}
            style={{
              padding: '10px 24px',
              backgroundColor: '#667eea',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer',
              fontWeight: 600
            }}
          >
            Go to Home
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: '#f9fafb',
      padding: '20px'
    }}>
      <div style={{
        backgroundColor: 'white',
        padding: '40px',
        borderRadius: '16px',
        maxWidth: '450px',
        width: '100%',
        boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
      }}>
        {/* Invitation Header */}
        <div style={{ textAlign: 'center', marginBottom: '32px' }}>
          <div style={{ fontSize: '4rem', marginBottom: '16px' }}>{invitation?.workspaceIcon || '👨‍👩‍👧'}</div>
          <h1 style={{ fontSize: '1.5rem', fontWeight: 700, marginBottom: '8px' }}>
            Join {invitation?.workspaceName}
          </h1>
          <p style={{ color: '#6b7280' }}>
            <strong>{invitation?.invitedBy}</strong> invited you to collaborate
          </p>
        </div>

        {/* Error Message */}
        {error && (
          <div style={{
            padding: '12px',
            backgroundColor: '#fef2f2',
            color: '#991b1b',
            borderRadius: '8px',
            marginBottom: '16px',
            fontSize: '0.875rem'
          }}>
            {error}
            {error.includes('different email') && (
              <button
                onClick={() => {
                  localStorage.removeItem('auth_token');
                  localStorage.removeItem('user_data');
                  localStorage.removeItem('token');
                  localStorage.removeItem('user');
                  window.location.reload();
                }}
                style={{
                  display: 'block',
                  marginTop: '12px',
                  padding: '8px 16px',
                  backgroundColor: '#dc2626',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  fontSize: '0.875rem',
                  cursor: 'pointer',
                  fontWeight: 600
                }}
              >
                Log Out & Try Again
              </button>
            )}
          </div>
        )}

        {/* If user has existing account */}
        {hasExistingAccount ? (
          <div>
            <div style={{
              padding: '16px',
              backgroundColor: '#f0fdf4',
              borderRadius: '8px',
              marginBottom: '24px',
              textAlign: 'center'
            }}>
              <p style={{ color: '#166534', fontSize: '0.875rem' }}>
                You already have an account with <strong>{invitation?.email}</strong>
              </p>
            </div>

            {isLoggedIn ? (
              <button
                onClick={handleAcceptAsExistingUser}
                disabled={submitting}
                style={{
                  width: '100%',
                  padding: '14px',
                  backgroundColor: '#22c55e',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '1rem',
                  fontWeight: 600,
                  cursor: submitting ? 'not-allowed' : 'pointer',
                  opacity: submitting ? 0.7 : 1
                }}
              >
                {submitting ? 'Joining...' : 'Join Workspace'}
              </button>
            ) : (
              <div>
                <p style={{ textAlign: 'center', color: '#6b7280', marginBottom: '16px' }}>
                  Please log in to join this workspace
                </p>
                <button
                  onClick={onLoginRequired}
                  style={{
                    width: '100%',
                    padding: '14px',
                    backgroundColor: '#667eea',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    fontSize: '1rem',
                    fontWeight: 600,
                    cursor: 'pointer'
                  }}
                >
                  Log In
                </button>
              </div>
            )}
          </div>
        ) : (
          /* Registration Form for new users */
          <div>
            <p style={{
              textAlign: 'center',
              color: '#6b7280',
              marginBottom: '24px',
              fontSize: '0.875rem'
            }}>
              Create an account to join with <strong>{invitation?.email}</strong>
            </p>

            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 500, marginBottom: '4px' }}>
                First Name
              </label>
              <input
                type="text"
                value={firstName}
                onChange={e => setFirstName(e.target.value)}
                placeholder="Your first name"
                style={{
                  width: '100%',
                  padding: '12px',
                  border: '1px solid #e5e7eb',
                  borderRadius: '8px',
                  fontSize: '0.875rem'
                }}
              />
            </div>

            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 500, marginBottom: '4px' }}>
                Last Name
              </label>
              <input
                type="text"
                value={lastName}
                onChange={e => setLastName(e.target.value)}
                placeholder="Your last name"
                style={{
                  width: '100%',
                  padding: '12px',
                  border: '1px solid #e5e7eb',
                  borderRadius: '8px',
                  fontSize: '0.875rem'
                }}
              />
            </div>

            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 500, marginBottom: '4px' }}>
                Password *
              </label>
              <input
                type="password"
                value={password}
                onChange={e => setPassword(e.target.value)}
                placeholder="Create a password (min 6 characters)"
                style={{
                  width: '100%',
                  padding: '12px',
                  border: '1px solid #e5e7eb',
                  borderRadius: '8px',
                  fontSize: '0.875rem'
                }}
              />
            </div>

            <div style={{ marginBottom: '24px' }}>
              <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 500, marginBottom: '4px' }}>
                Confirm Password *
              </label>
              <input
                type="password"
                value={confirmPassword}
                onChange={e => setConfirmPassword(e.target.value)}
                placeholder="Confirm your password"
                style={{
                  width: '100%',
                  padding: '12px',
                  border: '1px solid #e5e7eb',
                  borderRadius: '8px',
                  fontSize: '0.875rem'
                }}
              />
            </div>

            <button
              onClick={handleRegisterAndJoin}
              disabled={submitting || !password}
              style={{
                width: '100%',
                padding: '14px',
                backgroundColor: password ? '#22c55e' : '#d1d5db',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                fontSize: '1rem',
                fontWeight: 600,
                cursor: password && !submitting ? 'pointer' : 'not-allowed',
                opacity: submitting ? 0.7 : 1
              }}
            >
              {submitting ? 'Creating Account...' : 'Create Account & Join'}
            </button>

            <p style={{
              textAlign: 'center',
              marginTop: '16px',
              fontSize: '0.75rem',
              color: '#9ca3af'
            }}>
              By creating an account, you agree to our Terms of Service
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default JoinWorkspace;
